import { FileInputField, useForm } from "@sembark-travel/ui-form"
import { supportedDocuemntTypes } from "./store"
import { useState } from "react"

const MAX_FILE_NAME = 170

function extractFile(e: FileList | File[] | null): File | null {
	if (!e || Array.from(e).length === 0) return null
	return Array.from(e)[0]
}

function truncateFileName(file: File): File {
	if (file.name.length <= MAX_FILE_NAME) return file
	const ext = file.name.includes(".")
		? file.name.slice(file.name.lastIndexOf("."))
		: ""
	const base = file.name.slice(0, MAX_FILE_NAME - ext.length)
	return new File([file], base + ext, { type: file.type })
}

function validateFileSize(file: File): string | null {
	const isPDF = file.type === "application/pdf"
	const maxSize = isPDF ? 2 * 1024 * 1024 : 6 * 1024 * 1024 // 2MB for PDF, 6MB for images
	if (file.size > maxSize) {
		return isPDF
			? "PDF file size must be less than 2 MB."
			: "Image file size must be less than 6 MB."
	}
	return null
}

function validateFileType(file: File): string | null {
	return supportedDocuemntTypes.includes(file.type)
		? null
		: "Unsupported file type. Please upload JPG, JPEG, PNG, or PDF format."
}

function useDocumentFileHandler(name: string, onReset: () => void) {
	const form = useForm()

	return function handleFileChange(e: FileList | File[] | null) {
		let file = extractFile(e)
		if (!file) return
		file = truncateFileName(file)
		const error = validateFileType(file) ?? validateFileSize(file)
		if (error) {
			globalThis.alert(error)
			onReset()
			return
		}
		form.change(name, file)
		onReset()
	}
}

export function DocumentFileInputField({
	ref,
	id,
	name,
}: {
	ref?: React.RefObject<HTMLInputElement>
	id: string
	name: string
}) {
	const [fileKey, setFileKey] = useState(0)
	const resetInput = () => setFileKey((k) => k + 1)
	const handleFileChange = useDocumentFileHandler(name, resetInput)
	return (
		<FileInputField
			ref={ref}
			id={id}
			key={fileKey}
			onChange={handleFileChange}
			name={name}
			accept=".png,.jpg,.jpeg,.pdf"
			help="The image should be less than 6 MB (JPG, JPEG, or PNG), or the PDF should be less than 2 MB."
		/>
	)
}
